import randomSeed from 'random-seed';
import { GameMode } from '@/types/types';
import { getSeedString } from '../../../shared/dateHelper';

// Generate sequence based on day
const generateFixedNextLetters = (
  arrayLength: number,
  sequenceNumber: number,
  vowelThreshold: number
): string[] => {
  const seed = getSeedString(sequenceNumber);
  const rand = randomSeed.create(seed);

  const frequency: { [key: string]: number } = {
    Z: 0.00074,
    Q: 0.00095,
    X: 0.0012,
    J: 0.00153,
    K: 0.00772,
    V: 0.00978,
    B: 0.01492,
    P: 0.01929,
    Y: 0.01974,
    G: 0.02015,
    F: 0.02228,
    W: 0.0236,
    M: 0.02406,
    U: 0.02758,
    C: 0.02782,
    L: 0.04025,
    D: 0.04253,
    R: 0.05987,
    H: 0.06094,
    N: 0.06749,
    I: 0.06966,
    O: 0.07507,
    A: 0.08167,
    T: 0.09056,
    S: 0.10702,
    E: 0.12702,
  };

  const vowels = ['A', 'E', 'I', 'O', 'U'];

  function getRandomLetter(previousLetters: string[] = [], enforceVowel = false): string {
    const safeLetters = previousLetters ?? [];

    while (true) {
      let random = rand.floatBetween(0, 1);
      let total = 0;
      let generatedLetter = '';

      for (const letter in frequency) {
        total += frequency[letter];
        if (random <= total) {
          generatedLetter = letter;
          break;
        }
      }

      const lastTwoLetters = safeLetters.slice(-2);
      const lastThreeLetters = safeLetters.slice(-3);

      const isVowel = vowels.includes(generatedLetter);
      const lastTwoAreVowels = lastTwoLetters.every((l) => vowels.includes(l));
      const isRepeatedInLastThree = lastThreeLetters.includes(generatedLetter);

      if (enforceVowel) {
        if (!isVowel || isRepeatedInLastThree) continue;
      } else {
        if ((lastTwoAreVowels && isVowel) || isRepeatedInLastThree) continue;
      }

      return generatedLetter;
    }
  }

  const letters: string[] = [];
  for (let i = 0; i < arrayLength; i++) {
    if (
      i >= vowelThreshold &&
      !letters.slice(i - vowelThreshold, i).some((l) => vowels.includes(l))
    ) {
      letters.push(getRandomLetter(letters, true));
    } else {
      letters.push(getRandomLetter(letters));
    }
  }

  return letters;
};

export const getNextLetterArr = (mode: GameMode | undefined): string[] | undefined => {
  switch (mode) {
    case 'blitzSimple':
      return generateFixedNextLetters(2400, 1, 5);
    case 'blitzDeluxe':
      return generateFixedNextLetters(4000, 2, 5);
    case 'endlessSimple':
      return generateFixedNextLetters(10000, 3, 6);
    case 'endlessDeluxe':
      return generateFixedNextLetters(10000, 4, 6);
    default:
      console.warn('⚠️ getNextLetterArr: mode is undefined or invalid', mode);
      return undefined;
  }
};

export const getNextLetters = (mode: GameMode | undefined, moveCount: number): string[] => {
  const letters = getNextLetterArr(mode);
  if (!letters) return ['?', '?', '?'];
  return letters.slice(moveCount, moveCount + 3);
};

export const getNextLetter = (mode: GameMode | undefined, moveCount: number): string => {
  const letters = getNextLetterArr(mode);
  if (!letters || moveCount >= letters.length) return '?';
  return letters[moveCount];
};

export const getBonusLetter = (): string => {
  return generateFixedNextLetters(1, 4, 5)[0];
};
